import { useEffect, useState } from 'react';
import { EmptyState, ErrorState, PageHeader, PageLoading } from '../../components/ui/States';
import { api } from '../../lib/api';
import { errorMessage } from '../../hooks/useApi';
import { btnGhost, cardCls, formatDate, timeAgo } from './shared';

interface Entry {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  tenantId: string | null;
  meta: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
  actorId: string | null;
  actorEmail: string | null;
  actorName: string | null;
}
interface Page {
  entries: Entry[];
  nextBefore: string | null;
}

const SENTENCE: Record<string, string> = {
  'auth.login': 'signed in',
  'auth.login_failed': 'had a failed sign-in attempt',
  'auth.locked': 'was locked out after too many failed sign-ins',
  'auth.password_changed': 'changed their password',
  'title.approve': 'approved title',
  'title.reject': 'rejected title',
  'title.create': 'created title',
  'title.update': 'updated title',
  'creator.create': 'added creator',
  'creator.reset_password': 'reset the password for creator',
  'creator.block': 'changed the blocked status of creator',
  'tenant.create': 'added app',
  'tenant.suspended': 'changed the status of app',
  'api_key.create': 'created an API key',
  'api_key.revoke': 'revoked an API key',
  'episode.upload_video': 'uploaded a video for an episode',
  'episodes.bulk_create': 'added several episodes',
};

function metaText(meta: Entry['meta']): string | null {
  if (!meta) return null;
  for (const k of ['title', 'name', 'displayName', 'email', 'slug']) {
    const v = meta[k];
    if (typeof v === 'string' && v) return v;
  }
  return null;
}

export function ActivityLog() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    api<Page>('/admin/audit', { query: { limit: 50 } })
      .then((p) => {
        if (!live) return;
        setEntries(p.entries);
        setNext(p.nextBefore);
      })
      .catch((e) => live && setError(errorMessage(e)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [nonce]);

  const loadMore = async () => {
    if (!next) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const p = await api<Page>('/admin/audit', { query: { limit: 50, before: next } });
      setEntries((prev) => [...prev, ...p.entries]);
      setNext(p.nextBefore);
    } catch (e) {
      setMoreError(errorMessage(e));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <PageHeader
        title="Activity log"
        subtitle="A record of who did what on the platform, newest first."
        actions={
          <button onClick={() => setNonce((n) => n + 1)} disabled={loading} className={btnGhost}>
            Refresh
          </button>
        }
      />
      {loading ? (
        <PageLoading />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />
      ) : entries.length === 0 ? (
        <EmptyState title="Nothing has been recorded yet" message="Sign-ins and changes will appear here." />
      ) : (
        <>
          <ul className={`${cardCls} divide-y divide-[#241b33]`}>
            {entries.map((e) => {
              const who = e.actorName || e.actorEmail || 'System';
              const known = SENTENCE[e.action];
              const detail = metaText(e.meta);
              return (
                <li key={e.id} className="px-4 py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0 text-sm text-gray-200">
                    <span className="font-semibold text-white">{who}</span>{' '}
                    {known ?? <code className="text-xs text-violet-300">{e.action}</code>}
                    {detail && <span className="text-violet-300"> “{detail}”</span>}
                    {e.ipAddress && <span className="block text-[11px] text-gray-500">From {e.ipAddress}</span>}
                  </div>
                  <time className="text-xs text-gray-400 shrink-0 text-right" dateTime={e.createdAt} title={formatDate(e.createdAt)}>
                    {timeAgo(e.createdAt)}
                    <span className="block text-[11px] text-gray-500">{formatDate(e.createdAt)}</span>
                  </time>
                </li>
              );
            })}
          </ul>
          {moreError && <p className="mt-3 text-xs text-rose-400" role="alert">{moreError}</p>}
          {next && (
            <div className="flex justify-center mt-4">
              <button onClick={loadMore} disabled={loadingMore} className={btnGhost}>
                {loadingMore ? 'Loading…' : 'Load older'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
